/**
 * The header's status pill, replacing one that said "Online" unconditionally.
 *
 * That pill was hardcoded — a green dot and the word Online on every page, for
 * every user, regardless of whether anything worked. A platform connection
 * could lapse and the warehouse could fall days behind while it pulsed
 * reassuringly, which is the failure this component exists to end: the checks
 * already existed, nobody was ever shown them.
 *
 * Three states, and the third is the one that makes the other two worth
 * trusting:
 *   healthy  — checks ran and found nothing
 *   issues   — checks ran and found something, listed with a link to fix it
 *   unknown  — the checks could not run
 *
 * "unknown" is never rendered as healthy. A component that reports green when
 * it failed to look is worse than the hardcoded pill it replaces, because it
 * looks like it checked.
 *
 * @module components/SystemHealthIndicator
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AlertCircle, AlertTriangle, X } from "lucide-react";
import { getConnectedAccounts, getRecentSyncJobs } from "@/services/platformAccountService";
import { listBigQueryJobs } from "@/services/bigQueryJobService";
import { attentionItems } from "@/pages/admin/BigQuerySync";
import {
  bigQueryIssues,
  connectionIssues,
  syncQueueIssues,
  visibleIssues,
  type HealthIssue,
} from "@/services/systemHealth";
import { useIsPlatformMaster } from "@/hooks/useIsPlatformMaster";
import { logError } from "@/services/logger";

interface Props {
  workspaceId?: string;
}

type State =
  | { kind: "loading" }
  | { kind: "unknown" }
  | { kind: "ready"; issues: HealthIssue[] };

export const SystemHealthIndicator: React.FC<Props> = ({ workspaceId }) => {
  const { t } = useTranslation();
  // The hook is gated on having a session to check; without a workspace
  // there is nothing to report anyway.
  const { isMaster } = useIsPlatformMaster(Boolean(workspaceId));
  const [state, setState] = useState<State>({ kind: "loading" });
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    try {
      // Both reads, together: the account rows say whether a credential is
      // usable, and the job queue says whether anything is running at all.
      // Neither answers the other's question — every account read "active"
      // while six jobs sat unclaimed.
      const [accounts, jobs] = await Promise.all([
        getConnectedAccounts(workspaceId),
        getRecentSyncJobs(workspaceId),
      ]);
      const issues = [...connectionIssues(accounts), ...syncQueueIssues(jobs)];

      // Master-only, and failing softly on purpose: /api/bq-jobs is behind an
      // authorize-master gate, so a non-master reading it is a 401 rather than
      // an outage. A warehouse check that cannot run must not hide the
      // connection problems that CAN be reported.
      if (isMaster) {
        try {
          const result = await listBigQueryJobs();
          // A refused or failed read adds NOTHING rather than an empty
          // "all clear": the warehouse's health is simply unknown here, and
          // reporting it as fine is the exact habit this component replaces.
          if (result.success && result.data) {
            issues.push(...bigQueryIssues(attentionItems(result.data)));
          } else {
            logError("SystemHealth", "BigQuery job log unavailable", result.error);
          }
        } catch (e) {
          logError("SystemHealth", "BigQuery job log unavailable", e);
        }
      }

      setState({ kind: "ready", issues });
    } catch (e) {
      // Said out loud rather than shown as healthy.
      logError("SystemHealth", "health check failed", e);
      setState({ kind: "unknown" });
    }
  }, [workspaceId, isMaster]);

  useEffect(() => {
    void load();
  }, [load]);

  // Close on Escape and on a click outside — a popover that can only be closed
  // by finding its own small button again is worse than no popover.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  if (state.kind === "loading" || !workspaceId) return null;

  if (state.kind === "unknown") {
    return (
      <span
        role="status"
        className="hidden sm:inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700"
      >
        <span className="w-2 h-2 bg-neutral-400 rounded-full mr-2" aria-hidden="true" />
        {t("health.unknown")}
      </span>
    );
  }

  const shown = visibleIssues(state.issues, isMaster);

  if (shown.length === 0) {
    return (
      <span
        role="status"
        className="hidden sm:inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-success-100 text-success-800"
      >
        <span className="w-2 h-2 bg-success-500 rounded-full mr-2 animate-pulse" aria-hidden="true" />
        {t("health.ok")}
      </span>
    );
  }

  const errors = shown.filter((i) => i.severity === "error").length;
  const tone = errors > 0
    ? "bg-error-100 text-error-800 hover:bg-error-200"
    : "bg-amber-100 text-amber-900 hover:bg-amber-200";

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${tone}`}
      >
        {errors > 0 ? (
          <AlertCircle size={13} aria-hidden="true" />
        ) : (
          <AlertTriangle size={13} aria-hidden="true" />
        )}
        {/* The count is the point: it is what a person notices without opening
            anything, and it is why the number must match the list exactly. */}
        {t("health.issues", { count: shown.length })}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t("health.panelLabel")}
          className="absolute right-0 mt-2 w-80 max-w-[90vw] rounded-lg border border-neutral-200 bg-white shadow-lg z-40"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-100">
            <h2 className="text-sm font-semibold text-ink-900">{t("health.panelTitle")}</h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t("health.close")}
              className="text-neutral-700 hover:text-ink-900"
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>

          <ul className="max-h-80 overflow-y-auto divide-y divide-neutral-100">
            {shown.map((issue) => (
              <li key={issue.id} className="px-3 py-2.5">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-ink-900">
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      issue.severity === "error" ? "bg-error-500" : "bg-amber-500"
                    }`}
                    aria-hidden="true"
                  />
                  {issue.title}
                </p>
                <p className="mt-0.5 text-xs text-neutral-700">{issue.detail}</p>
                <Link
                  to={issue.href}
                  onClick={() => setOpen(false)}
                  className="mt-1 inline-block text-xs font-medium text-ink-900 hover:underline"
                >
                  {issue.action} →
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SystemHealthIndicator;
